import { useEffect, useState } from 'react';
import { PageBlueprint } from '@backstage/frontend-plugin-api';
import { useApi } from '@backstage/core-plugin-api';
import { Progress } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core/styles';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import catalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';
import {
  CatalogGraphPage,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import { stringifyEntityRef } from '@backstage/catalog-model';
import ShareIcon from '@material-ui/icons/Share';
import { GraphHealthProvider, HealthRenderNode } from '../datadog/GraphHealth';

// Fallback roots if the catalog query fails: every team in org/savr-org.yaml,
// expanding through every relation. A test holds this list to that file.
export const TEAM_ROOTS = [
  'group:default/lfant-bird',
  'group:default/team-core',
  'group:default/team-main',
  'group:default/platform',
  'group:default/team-frontend',
  'group:default/team-design',
];

const useGraphPageStyles = makeStyles(
  {
    // display: contents keeps the Page layout untouched while scoping the
    // rule. The header subtitle is the full comma-joined root list - useless
    // noise now that every component is a root - and CatalogGraphPage offers
    // no prop to change it.
    wrapper: {
      display: 'contents',
      '& header [class*="BackstageHeader-subtitle"]': {
        display: 'none',
      },
    },
  },
  { name: 'SavrCatalogGraphPage' },
);

/**
 * Default graph when opened from the sidebar (no query params): every
 * component, system and business process as a node, filtered to
 * dependsOn/partOf relations -
 * "the services and how they depend on each other" instead of the full org
 * fan-out through owners, users, APIs and resources. Opening the graph from
 * an entity page still works: query params override initialState per field.
 *
 * This must be set in code: the stock page:catalog-graph extension spreads
 * app-config values as top-level props, but CatalogGraphPage only reads
 * props.initialState, so config values are silently ignored (upstream bug in
 * plugin-catalog-graph 0.6.5).
 */
function SavrCatalogGraphPage() {
  const classes = useGraphPageStyles();
  const catalogApi = useApi(catalogApiRef);
  const [roots, setRoots] = useState<string[]>();
  const [fallback, setFallback] = useState(false);
  // Only the sidebar's default view (no query params) hides subcomponents -
  // deep links from entity pages keep the full picture. Evaluated once on
  // mount because the page itself writes filter state into the URL.
  const [isDefaultView] = useState(() => window.location.search === '');

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: [
          { kind: 'component' },
          { kind: 'system' },
          // Critical business processes sit above the systems that support
          // them, so the default graph reads process -> system -> vendor.
          { kind: 'businessprocess' },
        ],
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'spec.subcomponentOf',
        ],
      })
      .then(response => {
        if (!cancelled) {
          setRoots(
            response.items
              .filter(entity => !entity.spec?.subcomponentOf)
              .map(entity => stringifyEntityRef(entity)),
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFallback(true);
          setRoots(TEAM_ROOTS);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  if (!roots) {
    return <Progress />;
  }
  return (
    <div className={classes.wrapper}>
      <CatalogGraphPage
        initialState={
          fallback
            ? { rootEntityRefs: roots }
            : {
                rootEntityRefs: roots,
                selectedKinds: ['component', 'system', 'businessprocess'],
                // Both directions of each pair, so the filter holds whichever
                // way an edge is merged.
                selectedRelations: [
                  'dependsOn',
                  'dependencyOf',
                  'partOf',
                  'hasPart',
                ],
                // Every node is already a root, so depth 1 renders all edges
                // between them without fanning out further.
                maxDepth: 1,
              }
        }
        entityFilter={
          isDefaultView && !fallback
            ? entity => !entity.spec?.subcomponentOf
            : undefined
        }
        renderNode={HealthRenderNode}
      />
    </div>
  );
}

const catalogGraphPage = PageBlueprint.make({
  params: {
    path: '/catalog-graph',
    routeRef: catalogGraphPlugin.routes.catalogGraph,
    icon: <ShareIcon fontSize="inherit" />,
    title: 'Catalog Graph',
    // Nodes with ping checks get a live health glow (see ../datadog/GraphHealth).
    loader: async () => (
      <GraphHealthProvider>
        <SavrCatalogGraphPage />
      </GraphHealthProvider>
    ),
  },
});

// The entity pages' Relations card gets the same health-colored nodes
// (replaces the stock 'relations' entity card).
const healthRelationsCard = EntityCardBlueprint.make({
  name: 'relations',
  params: {
    loader: async () => (
      <GraphHealthProvider>
        <EntityCatalogGraphCard renderNode={HealthRenderNode} />
      </GraphHealthProvider>
    ),
  },
});

export const catalogGraphPluginWithOverrides = catalogGraphPlugin.withOverrides(
  {
    extensions: [catalogGraphPage, healthRelationsCard],
  },
);
